import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { ObjectAssetInfo } from "../api";
import {
  collectBrowseTags,
  countModelsByPrimaryTag,
  modelMatchesBrowseTag,
  type TagFilter,
} from "../lib/modelTags";

const MODEL_PAGE_SIZE = 100;

/**
 * Loads the full model catalog and applies tag + search filters.
 */
export function useModelsCatalog() {
  const [models, setModels] = useState<ObjectAssetInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tag, setTag] = useState<TagFilter>("all");

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items: ObjectAssetInfo[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const response = await api.listModels({
          page,
          pageSize: MODEL_PAGE_SIZE,
        });
        items.push(...response.items);
        hasMore = response.pagination.hasNext && response.items.length > 0;
        page++;
      }
      setModels(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const availableTags = useMemo(() => collectBrowseTags(models), [models]);

  const tagCounts = useMemo(() => countModelsByPrimaryTag(models), [models]);

  const filteredModels = useMemo(() => {
    const byTag =
      tag === "all"
        ? models
        : models.filter((m) => modelMatchesBrowseTag(m, tag));
    if (!searchQuery.trim()) return byTag;
    const q = searchQuery.toLowerCase();
    return byTag.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q) ||
        m.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [models, searchQuery, tag]);

  const resultSourceCount =
    tag === "all"
      ? models.length
      : (tagCounts.get(tag.trim().toLowerCase()) ?? 0);

  return {
    models,
    loading,
    error,
    fetchModels,
    searchQuery,
    setSearchQuery,
    tag,
    setTag,
    availableTags,
    tagCounts,
    filteredModels,
    resultSourceCount,
  };
}
